import { Component, EventEmitter, Input, NgZone, OnInit, Output } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { IconType } from '../../../icons/icons.component';
import { DOC_ORIENTATION, NgxImageCompressService } from 'ngx-image-compress';
import { ConfigService } from '../../../core/services/config.service';
import { StringHelper } from '../../../core/helpers/strings.helper';
import { GeneralService } from '../../../core/services/general.service';
import { ModalOptionsConfig, ModalService } from '../nz-modal-service/modal.service';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { MONIEPOINT_DESIGN_TYPE } from '../../../shared/constants';
import { MobileWebViewService } from '../../../core/services/mobile-web-view.service';
import { MobileDelegateService } from '../../../core/services/mobile-delegate.service';
import { DelegateType } from '../../../shared/models/utility.model';
import { OnboardingV2Service } from '../../../core/services/onboarding-v2.service';
import { DocumentTypeMap } from '../../../core/models/onboarding/onboarding-v2-model';
import { AppConfig } from '../../../shared/config/app-config';
import { DocumentSampleModalComponent } from '../document-sample-modal/document-sample-modal.component';
import { DashboardService } from '../../../core/services/dashboard.service';

@Component({
    selector: 'app-custom-image-uploader',
    templateUrl: './custom-image-uploader.component.html',
    styleUrls: ['./custom-image-uploader.component.scss'],
})
export class CustomImageUploaderComponent implements OnInit {
    @Input() defaultFileText = 'Drag your documents or photos here to start uploading';
    @Input() title: string;
    readonly MODAL_CLASS_NAME = 'moniepoint-dialog kamona-modal-alert';
    readonly MODAL_WIDTH = 396;
    loadingConfig = true;
    @Input() uploadType: string;
    @Input() fileTypeLabel = 'PNG, JPG, GIF, PDF';
    @Input() allowedFormats: string[] = ['.jpeg', '.png', '.pdf', '.jpg'];
    TYPE_ONE = 'TYPE_1';
    TYPE_TWO = 'TYPE_2';
    TYPE_THREE = 'TYPE_3';
    TYPE_FOUR = 'TYPE_4';
    TYPE_FIVE = 'TYPE_5';
    TYPE_SIX = 'TYPE_6';
    TYPE_SEVEN = 'TYPE_7';
    @Input() variant: number;
    ImageVariant = ImageVariant;
    @Input() hideTitle: boolean;
    @Input() icon: string;
    @Output() getFile = new EventEmitter();
    @Output() uploading = new EventEmitter();
    @Input() editLabel = 'Edit';
    @Input() viewLabel = 'View';
    fileName: string;
    @Input() customFile: CustomFileModel;
    @Input() convertToBase64: boolean;
    DEFAULT_ICON: IconType = 'upload-icon';
    CLIP_ICON: IconType = 'image-clip';
    uploadIcon: IconType;
    error = null;
    currentImageStatus;
    readonly DEFAULT_IMAGE_STATUS = 0;
    readonly UP_LOADING_IMAGE = 1;
    readonly UP_LOADED_SUCCESSFULLY = 2;
    readonly UP_LOAD_FAILED = 3;
    loadingPercentage = 0;
    @Input() maxFileSize: any; // In bytes
    readonly DEFAULT_IMAGE_LIMIT = 20971520;
    checkIcon: IconType = 'green-check';
    readonly KILO_BYTE = 1024000;
    readonly DEFAULT_MOBILE_MAX_FILE = 52428800;
    loading = false;
    loadingImage: boolean;
    runningModal: NzModalRef;
    file: File;
    sizeAlreadyChecked: boolean;
    @Input() useReferenceResponse: boolean;
    @Input() documentType: string;
    DocumentTypeMap = DocumentTypeMap;
    @Input() useKycMaxSize: boolean;
    @Input() useBrmPassportMaxSize: boolean;
    @Input() useValidateFileFormat: boolean;
    loadingProgressTimeout: any;
    readonly progressIncremeter = 5;
    readonly maxProgress = 95;
    documentV2: any;
    isPdf: boolean;
    @Input() extraModalSampleConfigClass: string;
    @Input() useDashboardServiceUpload: boolean;

    constructor(
        private imageCompress: NgxImageCompressService,
        private config: ConfigService,
        private generalService: GeneralService,
        private sanitizer: DomSanitizer,
        private appConfig: AppConfig,
        private modalService: ModalService,
        private mobileService: MobileWebViewService,
        private delegateService: MobileDelegateService,
        private onboardingService: OnboardingV2Service,
        public zone: NgZone,
        private dashboardService: DashboardService,
    ) {}

    ngOnInit(): void {
        this.initialize();
    }

    initialize() {
        if (!this.maxFileSize) {
            this.setDefaultSize();
        } else {
            this.preloadDetails();
            this.loadingConfig = false;
        }
    }

    formatDocumentType() {
        if (!this.documentType) {
            return;
        }
        let documentType = this.documentType.toUpperCase();
        documentType = documentType.replace('_', '');
        this.documentType = this.DocumentTypeMap.get(documentType);
    }

    preloadDetails() {
        this.setUploadIcon();
        this.preFillImageForm();
    }

    setDefaultSize() {
        this.config.configObj.subscribe((res) => {
            let maxFileSize: number;
            if (this.useBrmPassportMaxSize) {
                maxFileSize = res.brmOnboardingPassportMaxFileSize;
            } else if (this.useKycMaxSize) {
                maxFileSize = res?.kycMultipartSizeLimitData?.maxFileSize;
            } else {
                maxFileSize = res?.multipartSizeLimitData?.maxFileSize;
            }
            if (maxFileSize) {
                this.maxFileSize = maxFileSize;
            } else {
                this.maxFileSize = this.DEFAULT_IMAGE_LIMIT;
            }
            this.preloadDetails();
            this.loadingConfig = false;
        });
    }

    preFillImageForm() {
        let isValid: any = this.customFile?.name && this.customFile?.file;
        if (this.uploadType === this.TYPE_THREE || this.customFile?.hasReference) {
            isValid = !StringHelper.isNullOrEmptyOrUndefined(this.customFile?.name);
        }
        if (isValid) {
            this.setFileName(this.customFile?.name);
            if (this.uploadType === this.TYPE_FIVE) {
                this.viewFile(this.customFile?.fileReference);
            } else {
                this.currentImageStatus = this.UP_LOADED_SUCCESSFULLY;
            }
        } else {
            this.currentImageStatus = this.DEFAULT_IMAGE_STATUS;
        }
    }

    setFileName(name: string) {
        this.fileName = name;
    }

    setUploadIcon() {
        switch (this.icon) {
            case 'image-clip':
                this.uploadIcon = this.CLIP_ICON;
                break;
            default:
                this.uploadIcon = this.DEFAULT_ICON;
        }
    }

    stopProgressInterval() {
        clearInterval(this.loadingProgressTimeout);
    }

    setLoadingProgress() {
        this.loadingProgressTimeout = setInterval(() => {
            this.loadingPercentage = this.loadingPercentage + this.progressIncremeter;
            this.loadingPercentage = this.loadingPercentage > this.maxProgress ? this.maxProgress : this.loadingPercentage;
        }, 1000);
    }

    setSuccessfulView() {
        this.currentImageStatus = this.UP_LOADED_SUCCESSFULLY;
        this.stopProgressInterval();
        this.loading = false;
    }

    handleUploadFailure(error) {
        this.error = StringHelper.getErrorMessageHttpErrorResponse(error) || 'Document upload failed';
        this.loadingPercentage = 100;
        this.currentImageStatus = this.UP_LOAD_FAILED;
        this.setCustomFileModel(null, null, false);
        this.loading = false;
    }

    uploadDocument(file) {
        const isCorrectFileSize = this.maxFileSize > file?.size;
        if (!isCorrectFileSize) {
            this.handleFileSizeExceeded();
            return;
        }
        this.loadingPercentage = 10;
        this.setLoadingProgress();
        this.formatDocumentType();
        this.currentImageStatus = this.UP_LOADING_IMAGE;
        const documentType = this.documentType;
        const params: any = { file };
        if (documentType) {
            params.documentType = documentType;
        }

        let apiCall: Promise<any>;
        if(this.useDashboardServiceUpload) {
            apiCall = this.dashboardService.uploadFile(params);
        } else {
            apiCall = this.onboardingService.uploadDocumentV2(params);
        }

        apiCall.then((res) => {
                const fileName = file?.name;
                this.setFileName(fileName);
                this.customFile = {
                    name: fileName,
                    file,
                    fileReference: res?.fileReference,
                    type: res?.documentType,
                };
                this.getFile.emit(this.customFile);
                if (this.uploadType === this.TYPE_FIVE) {
                    this.viewFile(res?.fileReference);
                    return;
                }
                this.setSuccessfulView();
            })
            .catch((error) => {
                this.handleUploadFailure(error);
            });
    }

    onFileChange(event, uploadFile = []) {
        this.loading = true;
        this.uploading.emit(true);
        const [file] = event?.target?.files || uploadFile;
        const invalidFormat = !this.validateFileFormat(file, this.allowedFormats);
        if (this.useValidateFileFormat && invalidFormat) {
            this.uploading.emit(false);
            return;
        }
        this.file = file;
        if (this.useReferenceResponse) {
            this.uploadDocument(this.file);
            return;
        }
        const fileSize = file?.size;
        const isCorrectFileSize = this.sizeAlreadyChecked || this.maxFileSize > fileSize;
        if (isCorrectFileSize) {
            this.currentImageStatus = this.UP_LOADING_IMAGE;
            this.loadingPercentage = 5;
            this.error = null;
            this.loadingPercentage = 10;
            if (file) {
                this.loadingPercentage = 20;
                const fileName = file?.name;
                this.setFileName(fileName);
                this.loadingPercentage = 40;
                if (this.convertToBase64) {
                    this.getBase64(file, (img: string) => {
                        this.loadingPercentage = 60;
                        this.compressFile(img, fileName, true);
                    });
                } else {
                    this.loadingPercentage = 60;
                    this.setCustomFileModel(fileName, file, false);
                    this.loadingPercentage = 100;
                    this.currentImageStatus = this.UP_LOADED_SUCCESSFULLY;
                    this.loading = false;
                    this.uploading.emit(false);
                }
            }
        } else {
            this.error = StringHelper.isNullOrEmptyOrUndefined(fileSize) ? 'No file selected ' : `File larger than ${this.getMaxFileSize()}`;
            this.currentImageStatus = this.UP_LOAD_FAILED;
            this.setCustomFileModel(null, null, this.convertToBase64);
            this.loading = false;
            this.uploading.emit(false);
        }
    }

    validateFileFormat(file: File, formats: Array<string>) {
        const extension = file.name.split('.').pop();
        return formats.includes(`.${extension}`);
    }

    refileName(ref: string) {
        const refinedRef = this.getFileExtension(ref);
        if (refinedRef) {
            return '';
        }
        return '';
    }

    getFileExtension(ref) {
        let fileExtension = null;
        const refinedRef = ref?.toLowerCase();
        this.allowedFormats.forEach((e) => {
            if (refinedRef?.includes(e) && !fileExtension) {
                fileExtension = e;
            }
        });
        return fileExtension;
    }

    openFileExplorer(event: any) {
        if (this.loadingImage) {
            return;
        }
        const appVersionNumber = this.mobileService.getMobileAppVersion() || this.mobileService.getMoniepointAppParameters()?.appVersion;
        // alert(`appVersionNumber ---> ${appVersionNumber}`);
        const isLowerVersion = this.mobileService.isAppLowerVersion(this.mobileService.FILE_PICKER_APP_VERSION, `${appVersionNumber}`);
        // alert(`isLowerVersion ---> ${isLowerVersion}`);
        const isMobileApp = this.mobileService.isMobileWebViewUser();
        const isAndroid = this.mobileService.getMoniepointAppParameters()?.deviceRegistrationRequest?.model?.toLowerCase() === 'android';
        const canUseFilePicker = isMobileApp && isAndroid && !isLowerVersion;
        // alert(`canUseFilePicker  ---> ${canUseFilePicker}`);
        if (canUseFilePicker) {
            this.getFileNative();
            return;
        }
        event?.click();
    }

    getFileNative() {
        const allowableFormats = ['jpeg', 'png', 'pdf', 'jpg'];
        const maxFileSize = this.appConfig.USE_MOBILE_MAX_FILE_SIZE ? this.DEFAULT_MOBILE_MAX_FILE : this.maxFileSize;
        this.delegateService
            .setDelegateData(DelegateType.selectFiles, allowableFormats, maxFileSize)
            .then((res) => {
                const response = `${res}`;
                const isUploadCancel = response.includes(MobileFileUploadAction.USER_CANCELLED);
                if (isUploadCancel) {
                    return;
                }
                const isExceedMaxSize = response.includes(MobileFileUploadAction.MAX_FILE_SIZE);
                if (isExceedMaxSize) {
                    this.sizeAlreadyChecked = false;
                    this.handleFileSizeExceeded();
                    return;
                }
                const isEmtyFile = response.includes(MobileFileUploadAction.EMPTY_RESPONSE);
                this.handleSuccessfulMobileFileUpload(res, isEmtyFile);
            })
            .catch((error) => {
                this.handleFailedUpload();
            });
    }

    handleSuccessfulMobileFileUpload(res, isEmtyFile) {
        if (isEmtyFile) {
            this.handleFailedUpload();
            return;
        }
        const fileObj = JSON.parse(res);
        const encodedFile = `data:${fileObj?.contentType};base64,${fileObj?.encodedFile}`;
        const file = this.dataURLtoFile(encodedFile, fileObj?.fileName);
        this.sizeAlreadyChecked = true;
        this.onFileChange(null, [file]);
    }

    handleFileSizeExceeded() {
        this.error = `File larger than ${this.getMaxFileSize()}`;
        this.currentImageStatus = this.UP_LOAD_FAILED;
        this.setCustomFileModel(null, null, false);
        this.setFileName(null);
        this.loading = false;
    }

    handleFailedUpload(error = 'Failed to upload') {
        this.error = error;
        this.currentImageStatus = this.UP_LOAD_FAILED;
        this.setCustomFileModel(null, null, false);
        this.loading = false;
    }

    viewFile(ref) {
        if (this.loadingImage) {
            return;
        }
        this.loadingImage = true;
        this.uploading.emit(true);
        const title = this.title;
        const isOldFile = StringHelper.isNullOrEmptyOrUndefined(this.file);
        try {
            if (isOldFile) {
                this.viewFileWithReference(this.customFile.name, title);
            } else {
                const isPdfType = this.file?.type === 'application/pdf';
                const file = this.file;
                this.getBase64(file, (img: string) => {
                    this.viewDocument(img, title, isPdfType);
                    this.loadingImage = false;
                    this.uploading.emit(false);
                });
            }
        } catch (error) {
            this.launchAlert({
                title: 'Request Failed',
                description: StringHelper.getErrorMessageHttpErrorResponse(error),
            });
            this.loadingImage = false;
            this.uploading.emit(false);
        }
    }

    viewDocument(document, title, isPdf) {
        if (this.uploadType === this.TYPE_FIVE) {
            this.setDocumentDetails(document, isPdf);
            return;
        }
        this.launchModal({ document, title, isPdf }, DocumentSampleModalComponent);
    }

    setDocumentDetails(document, isPdf) {
        this.isPdf = isPdf;
        this.documentV2 = isPdf ? StringHelper.createObjectURL(document) : document;
        this.setSuccessfulView();
    }

    viewFileWithReference(ref, title) {
        this.generalService
            .getFileDto(ref)
            .then((response) => {
                const isContainPdfType = `${response?.base64File}`.includes('application/pdf');
                const isPdfType = response?.type === 'application/pdf' || isContainPdfType;
                this.viewDocument(response?.base64File, title, isPdfType);
                this.loadingImage = false;
                this.uploading.emit(false);
            })
            .catch((error) => {
                this.launchAlert({
                    title: 'Request Failed',
                    description: StringHelper.getErrorMessageHttpErrorResponse(error),
                });
                this.loadingImage = false;
                this.uploading.emit(false);
            });
    }

    launchAlert(params) {
        const config = new ModalOptionsConfig();
        config.nzComponentParams.alertType = params?.alertType || 'error';
        config.nzComponentParams.title = params?.title;
        config.className = this.MODAL_CLASS_NAME;
        config.nzWidth = this.MODAL_WIDTH;
        config.nzComponentParams.designType = MONIEPOINT_DESIGN_TYPE.KAMONA;
        if (params?.callbackName) {
            config.nzComponentParams.callbackName = params?.callbackName;
        }
        config.nzComponentParams.callback = () => {
            this.handleOncloseModal();
        };
        config.nzComponentParams.message = params?.description;
        this.runningModal = this.modalService.alert(config);
    }

    launchModal(params, component) {
        const config = this.createModalSampleConfig(params);
        this.runningModal = this.modalService.createModal(component, config);
    }

    createModalSampleConfig(params) {
        const config = new ModalOptionsConfig();
        config.nzComponentParams.data = params;
        config.className = 'hide-ant-modal';
        if (this.uploadType === this.TYPE_SIX && this.extraModalSampleConfigClass) {
            config.className += ` ${this.extraModalSampleConfigClass}`;
        }
        config.nzComponentParams.callback = () => {
            this.handleOncloseModal();
        };
        return config;
    }

    handleOncloseModal() {
        this.runningModal.destroy();
    }

    getMaxFileSize() {
        return this.uploadType === this.TYPE_SIX || this.uploadType === this.TYPE_SEVEN
            ? this.getMaxFileSizeV6()
            : `${(this.maxFileSize / this.KILO_BYTE).toFixed(1)}mb`;
    }

    getMaxFileSizeV6() {
        return `${(this.maxFileSize / this.KILO_BYTE).toFixed(0)}mb`;
    }

    getAllowableFiles() {
        let allowedFormats = [];
        allowedFormats = [...this.allowedFormats];
        let result = allowedFormats.toString();
        // result = result.replace(/,/g, ' ');
        result = result.replace(/\./g, ' ');
        return result;
    }

    dataURLtoFile(dataUrl, filename) {
        const arr = dataUrl.split(',');
        const mime = arr[0]?.match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr?.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr?.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
    }

    setCustomFileModel(fileName, file, isBase64) {
        this.customFile = {
            name: fileName,
            fileInBase64: isBase64 ? file : null,
            file: isBase64 ? null : file,
            hasReference: false,
        };
        this.getFile.emit(this.customFile);
    }

    compressFile(image, name, isBase64) {
        this.imageCompress.compressFile(image, DOC_ORIENTATION.Up, 50, 50).then((result) => {
            this.loadingPercentage = 80;
            this.setCustomFileModel(name, `${result}`, isBase64);
            this.loadingPercentage = 100;
            this.currentImageStatus = this.UP_LOADED_SUCCESSFULLY;
            this.loading = false;
        });
    }

    handleDragOver($event: DragEvent) {}

    clearImage() {
        this.currentImageStatus = this.DEFAULT_IMAGE_STATUS;
        this.fileName = null;
        this.customFile = {
            name: '',
            fileInBase64: null,
            file: null,
        };
        this.getFile.emit(this.customFile);
    }

    private getBase64(img: File, callback: (img: string) => void): void {
        const reader = new FileReader();
        // tslint:disable-next-line:no-non-null-assertion
        reader.addEventListener('load', () => callback(reader.result!.toString()));
        reader.readAsDataURL(img);
    }
}

export interface CustomFileModel {
    name: string;
    fileInBase64?: string;
    file?: File;
    hasReference?: boolean;
    fileReference?: string;
    type?: string;
}

export enum MobileFileUploadAction {
    MAX_FILE_SIZE = 'MAX_FILE_SIZE',
    USER_CANCELLED = 'USER_CANCELLED',
    EMPTY_RESPONSE = '{}',
}

export enum ImageVariant {
    VARIANT_1,
}
